import { useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { NAV } from '@/config/nav'
import { useSettings } from '@/context/SettingsContext'
import { Icon } from './icons'

/**
 * 路由匹配：路径一致，且 `to` 里带的 query 参数全部命中当前地址。
 *
 * 侧栏现在是一层入口，但入口仍可能带 query（如「AI 模特」= 商品图的模特特征）。
 * 判定逻辑抽成纯函数，是为了让高亮与页面内的参数消费不出现两套口径 ——
 * 两份判定早晚会分叉，症状是「点进去了但侧栏没亮」。
 */
export function matchTo(to: string, pathname: string, search: string): boolean {
  const [p, q] = to.split('?')
  if (p !== pathname) return false
  if (!q) return true
  const cur = new URLSearchParams(search)
  const want = new URLSearchParams(q)
  for (const [k, v] of want.entries()) if (cur.get(k) !== v) return false
  return true
}

/** 判断导航项是否为当前激活项（同时考虑 query 参数） */
function useActiveItemId(): string | null {
  const { pathname, search } = useLocation()
  /**
   * 带 query 的入口优先。
   *
   * 不加这层优先序会出真 bug：`商品图` → `/professional/products` 与
   * `AI 模特` → `/professional/products?feature=product-model` 是两个入口，
   * 访问后者时前者也能对上（`matchTo` 对不带 query 的 to 只比 pathname），
   * 结果两行同时点亮，用户看不出自己在哪。
   */
  return useMemo(() => {
    const all = NAV.flatMap((g) => g.items)
    const exact = all.find((it) => it.to.includes('?') && matchTo(it.to, pathname, search))
    return (exact ?? all.find((it) => matchTo(it.to, pathname, search)))?.id ?? null
  }, [pathname, search])
}

/**
 * 侧栏 —— **一层入口，没有任何折叠。**
 *
 * 2026-09-28 撤掉了两级折叠（分组头可折 + 子项可展）与「一键展开全部子项」按钮。
 * 三个理由：
 *   1. 折叠子项的第一步永远不是目的地，用户要点两次；
 *   2. 那些子项多半指向同一个页面、只是 query 不同（文案 6 条全进 /copywriting），
 *      摊在侧栏里就是同一页的三个门；
 *   3. 页面内本来就有 chips / tab 承载这些选择，侧栏再来一份是两份入口维护同一个东西。
 *
 * 现在：分组标题是静态小标签（不可点、不可折），下面是该组全部页面入口。
 *
 * 2026-09-28 二改：**撤掉侧栏搜索框**。搜索框是上一版为「55 个折叠子项」准备的
 * 找路工具；入口压到 14 个一层可见之后，它既扫不完 14 项（不需要），
 * 又占了侧栏最金贵的第一屏。功能级关键词已写进各入口的 `hint`（hover 可见）。
 */
export function Sidebar() {
  const { settings } = useSettings()
  const collapsed = settings.sidebarCollapsed
  const activeId = useActiveItemId()

  return (
    <aside className="sidebar">
      {/*
        品牌区兼「回首页」入口 —— 侧栏不再有「工作台首页」这一项。
        工具与账本（首页 / 任务中心 / 模板 / 作品 / 用量）全部搬到了顶栏，
        见 nav.HEADER_ENTRIES；这里用 logo 承接首页，是通行做法，
        折叠状态下只剩品牌标本身（没有文字），仍然可点。
      */}
      <NavLink to="/" className="brand" title="回到工作台首页" aria-label="回到工作台首页">
        {/*
          品牌标 = public/brand-mark.svg，**与 favicon 同一枚文件**。
          这里刻意用 <img> 而不是内联 SVG：内联等于把形状抄第二份，
          改 favicon 时侧栏不跟着变，两处形状就会慢慢漂移。
          alt="" 是故意的 —— 可点区域已有 aria-label，右侧文字也写了
          「华数云工作台」，这个标是装饰，不重复播报。
        */}
        <img className="brand__mark" src="/brand-mark.svg" alt="" width={30} height={30} />
        {!collapsed ? (
          <div className="brand__text">
            <div className="brand__title" style={{ fontSize: '18px'}}>华数云工作台</div>
            {/* <div className="brand__sub">ECOMMERCE STUDIO</div> */}
          </div>
        ) : null}
      </NavLink>

      <nav className="nav">
        {NAV.map((group) => (
          <div className="nav-group" key={group.id}>
            <div className="nav-group__label">{group.label}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                className="nav-item"
                data-active={activeId === item.id}
                title={[item.label, item.hint].filter(Boolean).join(' · ')}
              >
                <Icon name={item.icon} size={16} className="nav-item__icon" />
                <span className="nav-item__text">{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  )
}
